import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ImageUploader = ({ urls = [], onChange, max = 10 }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (urls.length + files.length > max) {
      toast.error(`Maximum ${max} images`);
      return;
    }
    setBusy(true);
    const newUrls = [...urls];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newUrls.push(`${BACKEND_URL}${data.url}`);
      } catch (err) {
        toast.error(`Erreur upload ${f.name} : ${formatApiError(err)}`);
      }
    }
    onChange(newUrls);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (i) => onChange(urls.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {urls.map((u, i) => (
          <div key={i} className="relative group aspect-square rounded-xl overflow-hidden bg-[#F5F0EB] border border-[#E5E5E5]">
            <img src={u} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(i)}
              className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid="remove-photo-btn"
            >
              <X className="w-4 h-4" />
            </button>
            {i === 0 && (
              <span className="absolute bottom-1 left-1 text-[10px] px-2 py-0.5 rounded-full bg-[#D6202C] text-white">
                Principale
              </span>
            )}
          </div>
        ))}
        {urls.length < max && (
          <button
            type="button"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            data-testid="upload-photo-btn"
            className="aspect-square rounded-xl border-2 border-dashed border-[#E5E5E5] hover:border-[#D6202C] hover:bg-[#FEF2F3] transition-colors flex flex-col items-center justify-center text-sm text-muted-foreground gap-2"
          >
            {busy ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="text-xs">Envoi...</span>
              </>
            ) : (
              <>
                <Upload className="w-6 h-6" strokeWidth={1.5} />
                <span className="text-xs">Ajouter</span>
              </>
            )}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="hidden"
        onChange={handleSelect}
      />
      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <ImageIcon className="w-3 h-3" /> JPG/PNG/WEBP · 5 Mo max · La 1ère sera l'image principale
      </p>
    </div>
  );
};
